import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import type { Me } from './api/types';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { RouteAnnouncer, SkipLink } from './components/a11y';
import { NavBar } from './components/NavBar';
import { PublicShell } from './components/PublicShell';
import { RouteMeta } from './components/RouteMeta';
import { Admin } from './pages/Admin';
import { AuthCallback } from './pages/AuthCallback';
import { Calendar } from './pages/Calendar';
import { Home } from './pages/Home';
import { Journal } from './pages/Journal';
import { Leaderboard } from './pages/Leaderboard';
import { Library } from './pages/Library';
import { Login } from './pages/Login';
import { Onboarding } from './pages/Onboarding';
import { Practice } from './pages/Practice';
import { Practices } from './pages/Practices';
import { Settings } from './pages/Settings';
import { EditorialStandards } from './pages/public/EditorialStandards';
import { HowItWorks } from './pages/public/HowItWorks';
import { Landing } from './pages/public/Landing';
import { Pricing } from './pages/public/Pricing';
import { Privacy } from './pages/public/Privacy';
import { PublicSample } from './pages/public/PublicSample';
import { Support } from './pages/public/Support';
import { Terms } from './pages/public/Terms';
import { UserProvider, useUser } from './state/UserContext';
import { ThemeProvider } from './theme/ThemeProvider';

function FullPageSpinner() {
  const { t } = useTranslation();
  return (
    <div className="center-page" role="status">
      <div className="spinner" aria-hidden="true" />
      <span className="visually-hidden">{t('common.loading')}</span>
    </div>
  );
}

/** Review fix F4: onboarding completion is its own profile fact, independent of `traditions`
 * (empty = explore all). Falls back to the legacy "has a tradition" rule only if an older backend
 * doesn't send the field yet, so a frontend-first deploy can't bounce everyone to /onboarding. */
function isOnboarded(me: Me): boolean {
  return me.onboardingComplete ?? me.traditions.length > 0;
}

/** Gates the signed-in app behind Cognito auth (or the mock auto-login). A signed-out visitor to
 * a protected URL sees sign-in in place, so the deep link still works after signing in. */
function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Login />;

  return <Outlet />;
}

/** Wires up the per-user data + theming context once we know the user is authenticated. */
function AppProviders() {
  return (
    <UserProvider>
      <ThemeProvider>
        <Outlet />
      </ThemeProvider>
    </UserProvider>
  );
}

/** Redirects to Onboarding until onboarding has been completed -- never merely because the
 * traditions list is empty, and never again after Settings changes. */
function RequireOnboarding() {
  const { me, isLoading } = useUser();

  if (isLoading) return <FullPageSpinner />;
  if (me && !isOnboarded(me)) return <Navigate to="/onboarding" replace />;

  return <Outlet />;
}

function Layout() {
  return (
    <div className="app-shell">
      <SkipLink />
      <NavBar />
      <main id="main-content" className="app-content" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

function RootRedirect() {
  const { me, isLoading } = useUser();

  if (isLoading) return <FullPageSpinner />;
  if (me && !isOnboarded(me)) return <Navigate to="/onboarding" replace />;

  return <Navigate to="/home" replace />;
}

/** `/`: the public landing page for signed-out visitors; signed-in users still go to Today. */
function RootGate() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) {
    return (
      <PublicShell>
        <Landing />
      </PublicShell>
    );
  }
  return (
    <UserProvider>
      <ThemeProvider>
        <RootRedirect />
      </ThemeProvider>
    </UserProvider>
  );
}

function PublicLayout() {
  return (
    <PublicShell>
      <Outlet />
    </PublicShell>
  );
}

/** `/login` and `/signup`: a signed-in visitor is sent on to the app instead. */
function LoginRoute({ mode }: { mode: 'signin' | 'signup' }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullPageSpinner />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <Login key={mode} initialMode={mode} />;
}

export function App(): ReactNode {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RouteMeta />
        <RouteAnnouncer />
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/" element={<RootGate />} />
          <Route path="/login" element={<LoginRoute mode="signin" />} />
          <Route path="/signup" element={<LoginRoute mode="signup" />} />

          {/* Public website (review fix F11) -- outside RequireAuth. */}
          <Route element={<PublicLayout />}>
            <Route path="/welcome" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/editorial-standards" element={<EditorialStandards />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/support" element={<Support />} />
            <Route path="/sample" element={<PublicSample />} />
          </Route>

          <Route element={<RequireAuth />}>
            <Route element={<AppProviders />}>
              <Route path="/onboarding" element={<Onboarding />} />

              <Route element={<RequireOnboarding />}>
                <Route element={<Layout />}>
                  <Route path="/home" element={<Home />} />
                  <Route path="/practices" element={<Practices />} />
                  <Route path="/practice/:id" element={<Practice />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/library" element={<Library />} />
                  <Route path="/journal" element={<Journal />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/admin" element={<Admin />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
